import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { DropResult } from 'react-beautiful-dnd'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import {
  BrgButton,
  ButtonVariant,
  CommonInput,
  INPUT_TYPES,
  useToasts
} from '@brg/ui'
import PlusIconThin from 'shared/icons/PlusIconThin'
import RadioButton from 'shared/components/Radio/RadioButton'
import { CommonCheckbox } from 'shared/components/CommonCheckbox/CommonCheckbox'
import { useTranslation } from 'react-i18next'
import {
  addManagerQuestion,
  buildManagerQuestionAddPayload,
  fetchManagerQuestionList,
  parseManagerQuestionAddResponse,
  shouldUseManagerQuestionUpdate,
  unwrapManagerQuestionListPayload,
  updateManagerQuestion
} from 'shared/api/managerQuestion'
import { buildManagerQuestionnaireQuestionPayload } from 'shared/api/managerQuestionnaireQuestion'
import { mapApiQuestionsToReportDrafts } from './hydrateTestFromApi'
import { uploadTestBlockImage } from 'shared/api/uploadTestBlockImage'
import { resolvedRemoteImageSrc } from 'shared/utils/resolveFilesBurgdevUrl'
import {
  buildScaleCols,
  createEmptyQuestionDraft,
  surveyFieldsForUiType,
  surveyKindToUiType,
  SURVEY_UI_TYPE_OPTIONS,
  type AnswerMode,
  type AnswerOption,
  type ProductKind,
  type QuestionDraft,
  type SurveyQuestionKind,
  type SurveyUiType
} from './questionTypes'
import {
  countFilledOptions,
  isQuestionValidForSave
} from './questionDraftValidation'
import { useFileObjectUrl } from './useFileObjectUrl'
import { InfoIcon } from 'shared/components/InfoIcon/InfoIcon'
import {
  QuestionsSectionTitle,
  QuestionsDroppableList,
  QuestionFormCard,
  QuestionCardDragRow,
  SavedQuestionRoot,
  SavedDesktopHandle,
  SavedBody,
  SavedDesktopKindLabel,
  SavedMobileKindLabel,
  SavedActions,
  SavedActionBtn,
  SavedQuestionTitle,
  ShowFullToggle,
  SavedQuestionImageCenter,
  SavedQuestionImage,
  SavedOptionsStack,
  SavedOptionRow,
  SavedOptionBody,
  SavedOptionText,
  SavedOptionMark,
  SavedExplanationSection,
  SavedExplanationLabel,
  SavedExplanationText,
  FormBlock,
  SurveyTypeDropdownWrap,
  FieldLabel,
  InlineFieldLabel,
  LabelRow,
  QuestionTextRow,
  QuestionInputColumn,
  QuestionTextareaWrap,
  QuestionTextarea,
  CharCounter,
  ImagePreviewBox,
  AttachedImageRow,
  ImagePreviewImg,
  SubTitle,
  MarkCorrectHintRow,
  MarkCorrectHintDummy,
  MarkCorrectHintLabel,
  RadioStack,
  OptionsList,
  OptionRow,
  OptionFieldColumn,
  OptionImagePreviewImg,
  OptionActions,
  DragHandle,
  OptionInput,
  OptionMark,
  IconBtn,
  ExplanationWrap,
  ExplanationTextarea,
  FormActions,
  HiddenFileInput,
  UiCheckboxWrap,
  AddTagButton
} from './questionsTabStyles'

const uid = () =>
  `q-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`

const QUESTION_TEXT_MAX = 100
const QUESTIONNAIRE_TEXT_MAX = 4000
const EXPLANATION_MAX = 500
const MAX_ANSWER_OPTIONS = 10
const DND_QUESTION_TYPE = 'tc-question'
const DND_OPTION_TYPE = 'tc-option'

function isAnswerOptionsCountError(message: string): boolean {
  const lower = message.toLowerCase()
  return (
    lower.includes('answeroptions') ||
    (lower.includes('answer') &&
      lower.includes('option') &&
      lower.includes('10'))
  )
}

const QuestionImagePreview: FC<{
  file: File | null
  remoteUrl?: string | null
}> = ({ file, remoteUrl }) => {
  const blobUrl = useFileObjectUrl(file)
  const src = blobUrl ?? resolvedRemoteImageSrc(remoteUrl)
  if (!src) return null
  return (
    <ImagePreviewBox>
      <ImagePreviewImg src={src} alt="" />
    </ImagePreviewBox>
  )
}

const OptionImagePreview: FC<{
  file: File | null
  remoteUrl?: string | null
}> = ({ file, remoteUrl }) => {
  const blobUrl = useFileObjectUrl(file)
  const src = blobUrl ?? resolvedRemoteImageSrc(remoteUrl)
  if (!src) return null
  return <OptionImagePreviewImg src={src} alt="" />
}

const makeOption = (): AnswerOption => ({
  id: uid(),
  text: '',
  correct: false,
  imageFile: null,
  imageUrl: null,
  optionFileContainerId: null
})

const makeQuestion = (kind: SurveyQuestionKind = 'single'): QuestionDraft =>
  createEmptyQuestionDraft(kind)

function reorder<T>(list: T[], startIndex: number, endIndex: number): T[] {
  const result = [...list]
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)
  return result
}

const RemoveImageIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
  >
    <path
      d="M6 6L18 18M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
)

const ImageIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M21 9C21 10.6569 19.6569 12 18 12C16.3431 12 15 10.6569 15 9C15 7.34315 16.3431 6 18 6C19.6569 6 21 7.34315 21 9ZM19.65 9C19.65 9.91127 18.9113 10.65 18 10.65C17.0887 10.65 16.35 9.91127 16.35 9C16.35 8.08873 17.0887 7.35 18 7.35C18.9113 7.35 19.65 8.08873 19.65 9Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.25 3C1.00736 3 0 4.00736 0 5.25V18.75C0 19.9926 1.00736 21 2.25 21H21.75C22.9926 21 24 19.9926 24 18.75V5.25C24 4.00736 22.9926 3 21.75 3H2.25ZM21.75 4.35H2.25C1.75294 4.35 1.35 4.75294 1.35 5.25V17.7473L7.25779 10.7726C7.84463 10.0797 8.92108 10.1051 9.47465 10.8248L13.1152 15.5577C13.1403 15.5903 13.1869 15.5966 13.2198 15.5718L15.4206 13.911C15.934 13.5236 16.6432 13.5281 17.1517 13.9219L22.65 18.1813V5.25C22.65 4.75294 22.2471 4.35 21.75 4.35ZM2.25 19.65C2.03366 19.65 1.83515 19.5737 1.67993 19.4465L8.28791 11.6451C8.3188 11.6086 8.37545 11.61 8.40459 11.6479L12.0451 16.3808C12.5218 17.0005 13.4089 17.1204 14.033 16.6494L16.2338 14.9886C16.2608 14.9682 16.2982 14.9684 16.3249 14.9892L22.1919 19.5342C22.0614 19.6079 21.9106 19.65 21.75 19.65H2.25Z"
      fill="currentColor"
    />
  </svg>
)

const TrashIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="25"
    viewBox="0 0 20 25"
    fill="none"
    aria-hidden
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.80005 2C5.80005 0.89543 6.69548 0 7.80005 0H11.8C12.9046 0 13.8 0.895431 13.8 2V3C13.8 3.45033 13.6512 3.8659 13.4 4.2002H18.8C19.2418 4.2002 19.6 4.55837 19.6 5.0002V9.0002C19.6 9.44202 19.2418 9.8002 18.8 9.8002H17.6V23.0002C17.6 23.9943 16.7941 24.8002 15.8 24.8002H3.8C2.80589 24.8002 2 23.9943 2 23.0002V9.8002H0.8C0.358173 9.8002 0 9.44202 0 9.0002V5.0002C0 4.55837 0.358172 4.2002 0.8 4.2002H6.20005C5.94888 3.8659 5.80005 3.45033 5.80005 3V2ZM7.80005 1.8H11.8C11.9105 1.8 12 1.88954 12 2V3C12 3.11046 11.9105 3.2 11.8 3.2H7.80005C7.68959 3.2 7.60005 3.11046 7.60005 3V2C7.60005 1.88954 7.68959 1.8 7.80005 1.8ZM1.6 5.8002H18V8.2002H1.6V5.8002ZM16 9.8002V23.2002H3.6V9.8002H16Z"
      fill="currentColor"
    />
  </svg>
)

const EditLineIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.2166 2.43934C17.8023 1.85355 18.7521 1.85355 19.3379 2.43934L21.4237 4.52513C22.0094 5.11091 22.0094 6.06066 21.4237 6.64645L7.33668 20.7334C7.13496 20.9352 6.8799 21.0752 6.60141 21.1371L3.21808 21.8889C2.47245 22.0546 1.82384 21.357 2.04333 20.6254L3.22586 16.6836C3.29719 16.4458 3.42641 16.2295 3.60194 16.054L17.2166 2.43934ZM18.6308 3.14645C18.4355 2.95118 18.1189 2.95118 17.9237 3.14645L15.9843 5.08579L18.7772 7.87868L20.7166 5.93934C20.9118 5.74408 20.9118 5.4275 20.7166 5.23223L18.6308 3.14645ZM18.0701 8.58579L15.2772 5.79289L4.30904 16.7611C4.25053 16.8196 4.20746 16.8917 4.18368 16.9709L3.00115 20.9127L6.38448 20.1609C6.47731 20.1402 6.56233 20.0936 6.62957 20.0263L18.0701 8.58579Z"
      fill="currentColor"
    />
  </svg>
)

const DragDotsIcon: FC = () => (
  <svg width="16" height="20" viewBox="0 0 16 20" fill="none" aria-hidden>
    {[0, 1, 2].map((row) => (
      <g key={row} transform={`translate(0 ${row * 7})`}>
        <circle cx="4" cy="3" r="1.5" fill="currentColor" />
        <circle cx="12" cy="3" r="1.5" fill="currentColor" />
      </g>
    ))}
  </svg>
)

const SavedQuestionImageLarge: FC<{
  file: File | null
  remoteUrl?: string | null
}> = ({ file, remoteUrl }) => {
  const blobUrl = useFileObjectUrl(file)
  const src = blobUrl ?? resolvedRemoteImageSrc(remoteUrl)
  if (!src) return null
  return (
    <SavedQuestionImageCenter>
      <SavedQuestionImage src={src} alt="" />
    </SavedQuestionImageCenter>
  )
}

type SavedQuestionBlockProps = {
  q: QuestionDraft
  dragHandleProps: Record<string, unknown>
  onDelete: () => void
  onEdit: () => void
  t: (key: string) => string
  isQuestionnaire?: boolean
}

const draftKind = (q: QuestionDraft): SurveyQuestionKind =>
  q.kind ?? (q.answerMode === 'multiple' ? 'multiple' : 'single')

const savedKindLabelKey = (kind: SurveyQuestionKind): string => {
  const uiType = surveyKindToUiType(kind)
  if (uiType === 'standard') {
    return 'modal.test_create.questions.saved_kind_standard'
  }
  const option = SURVEY_UI_TYPE_OPTIONS.find((item) => item.uiType === uiType)
  return option?.labelKey ?? 'modal.test_create.questions.saved_kind_standard'
}

const ShowFullChevron: FC = () => (
  <svg
    width="16"
    height="9"
    viewBox="0 0 28 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path d="M14 12.316L25.4804 0.446936C26.0568 -0.148985 26.9913 -0.148977 27.5677 0.446951C28.1441 1.04288 28.1441 2.00906 27.5677 2.60498L15.0437 15.5531C14.4673 16.149 13.5327 16.149 12.9563 15.5531L0.432308 2.60498C-0.144096 2.00906 -0.144102 1.04288 0.432297 0.44695C1.00869 -0.148978 1.94323 -0.148986 2.51963 0.446935L14 12.316Z" />
  </svg>
)

const ExpandableSavedQuestionTitle: FC<{
  text: string
  t: (key: string) => string
}> = ({ text, t }) => {
  const ref = useRef<HTMLDivElement>(null)
  const [collapsed, setCollapsed] = useState(true)
  const [overflows, setOverflows] = useState(false)
  const display = text.trim() ? text : '—'

  useLayoutEffect(() => {
    setCollapsed(true)
  }, [text])

  useLayoutEffect(() => {
    const el = ref.current
    if (!el || !collapsed) {
      return undefined
    }
    const measure = () => {
      setOverflows(el.scrollHeight > el.clientHeight + 1)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') {
      return undefined
    }
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [text, collapsed])

  return (
    <div>
      <SavedQuestionTitle ref={ref} $collapsed={collapsed}>
        {display}
      </SavedQuestionTitle>
      {overflows ? (
        <ShowFullToggle
          type="button"
          $expanded={!collapsed}
          onClick={() => setCollapsed((v) => !v)}
        >
          <span>
            {t(
              collapsed
                ? 'modal.test_create.questions.show_full'
                : 'modal.test_create.questions.hide_full'
            )}
          </span>
          <ShowFullChevron />
        </ShowFullToggle>
      ) : null}
    </div>
  )
}

const SavedQuestionBlock: FC<SavedQuestionBlockProps> = ({
  q,
  dragHandleProps,
  onDelete,
  onEdit,
  t,
  isQuestionnaire = false
}) => {
  const kind = draftKind(q)
  const kindLabel = t(savedKindLabelKey(kind))
  const showChoiceOptions =
    kind === 'single' || kind === 'multiple' || kind === 'dropdown'
  const showScaleSummary = kind === 'scale'
  const showMatrixSummary = kind === 'matrix'

  return (
    <SavedQuestionRoot>
      <SavedDesktopHandle>
        <DragHandle
          type="button"
          {...dragHandleProps}
          aria-label={t('modal.test_create.questions.drag_handle')}
        >
          <DragDotsIcon />
        </DragHandle>
      </SavedDesktopHandle>

      <SavedMobileKindLabel>{kindLabel}</SavedMobileKindLabel>

      <SavedBody>
        <SavedDesktopKindLabel>{kindLabel}</SavedDesktopKindLabel>

        <ExpandableSavedQuestionTitle text={q.text} t={t} />

        <SavedQuestionImageLarge file={q.imageFile} remoteUrl={q.imageUrl} />

        {showChoiceOptions ? (
          <SavedOptionsStack>
            {q.options.map((opt) => (
              <SavedOptionRow key={opt.id}>
                {!isQuestionnaire &&
                  (q.answerMode === 'single' ? (
                    <SavedOptionMark
                      type="radio"
                      name={`saved-correct-${q.id}`}
                      checked={opt.correct}
                      readOnly
                      tabIndex={-1}
                    />
                  ) : (
                    <UiCheckboxWrap>
                      <CommonCheckbox
                        checked={Boolean(opt.correct)}
                        disabled
                        onClick={() => {}}
                      />
                    </UiCheckboxWrap>
                  ))}
                <SavedOptionBody>
                  <SavedOptionText>
                    {opt.text.trim() ? opt.text : '—'}
                  </SavedOptionText>
                  {(kind === 'single' || kind === 'multiple') &&
                  (opt.imageFile || opt.imageUrl) ? (
                    <OptionImagePreview
                      file={opt.imageFile}
                      remoteUrl={opt.imageUrl}
                    />
                  ) : null}
                </SavedOptionBody>
              </SavedOptionRow>
            ))}
          </SavedOptionsStack>
        ) : null}

        {showScaleSummary ? (
          <SavedExplanationSection>
            <SavedExplanationText>
              {t('modal.test_create.questions.scale_summary')
                .replace('{{min}}', String(q.scaleMin ?? 1))
                .replace('{{max}}', String(q.scaleMax ?? 5))}
            </SavedExplanationText>
          </SavedExplanationSection>
        ) : null}

        {showMatrixSummary ? (
          <SavedOptionsStack>
            {(q.matrixRows ?? [])
              .filter((r) => r.trim())
              .map((row, i) => (
                <SavedOptionRow key={`${q.id}-row-${i}`}>
                  <SavedOptionBody>
                    <SavedOptionText>{row}</SavedOptionText>
                  </SavedOptionBody>
                </SavedOptionRow>
              ))}
          </SavedOptionsStack>
        ) : null}

        {!isQuestionnaire && q.explanation.trim() !== '' && (
          <SavedExplanationSection>
            <SavedExplanationLabel>
              {t('modal.test_create.questions.explanation_label')}
            </SavedExplanationLabel>
            <SavedExplanationText>{q.explanation}</SavedExplanationText>
          </SavedExplanationSection>
        )}
      </SavedBody>

      <SavedActions>
        <SavedActionBtn
          type="button"
          aria-label={t('modal.test_create.questions.edit_question')}
          onMouseDown={(e) => {
            e.stopPropagation()
          }}
          onClick={(e) => {
            e.stopPropagation()
            onEdit()
          }}
        >
          <EditLineIcon />
        </SavedActionBtn>
        <SavedActionBtn
          type="button"
          aria-label={t('modal.test_create.questions.delete_question')}
          onMouseDown={(e) => {
            e.stopPropagation()
          }}
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
        >
          <TrashIcon />
        </SavedActionBtn>
      </SavedActions>
    </SavedQuestionRoot>
  )
}

type FilePick =
  | { kind: 'question'; qId: string }
  | { kind: 'option'; qId: string; oId: string }

const TestCreateQuestionsTab: FC<{
  productKind?: ProductKind
  testItemId?: string | null
  accessToken?: string | null
  longreadId?: string | null
  onQuestionsChange?: (questions: QuestionDraft[]) => void
  initialQuestions?: QuestionDraft[] | null
}> = ({
  productKind = 'test',
  testItemId,
  accessToken,
  longreadId,
  onQuestionsChange,
  initialQuestions
}) => {
  const isQuestionnaire = productKind === 'questionnaire'
  const questionTextMax = isQuestionnaire
    ? QUESTIONNAIRE_TEXT_MAX
    : QUESTION_TEXT_MAX
  const { t, i18n } = useTranslation()
  const toasts = useToasts()
  const tq = useCallback(
    (key: string) => {
      if (isQuestionnaire) {
        const surveyKey = `${key}_survey`
        if (i18n.exists(surveyKey)) {
          return t(surveyKey)
        }
      }
      return t(key)
    },
    [isQuestionnaire, i18n, t]
  )
  const [questions, setQuestions] = useState<QuestionDraft[]>(() => {
    if (initialQuestions && initialQuestions.length > 0) {
      return initialQuestions
    }
    return [makeQuestion()]
  })
  const questionsRef = useRef(questions)
  questionsRef.current = questions

  useEffect(() => {
    onQuestionsChange?.(questions)
  }, [questions, onQuestionsChange])

  const [savingQuestionId, setSavingQuestionId] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const [filePick, setFilePick] = useState<FilePick | null>(null)

  const surveyTypeOptions = useMemo(
    () =>
      SURVEY_UI_TYPE_OPTIONS.map((o) => ({
        value: o.uiType,
        label: t(o.labelKey)
      })),
    [t]
  )

  const openFilePicker = useCallback((pick: FilePick) => {
    setFilePick(pick)
    requestAnimationFrame(() => fileRef.current?.click())
  }, [])

  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file || !filePick) {
        e.target.value = ''
        return
      }
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.id !== filePick.qId) return q
          if (filePick.kind === 'question') {
            return {
              ...q,
              imageFile: file,
              imageUrl: null,
              questionFileContainerId: null
            }
          }
          return {
            ...q,
            options: q.options.map((o) =>
              o.id === filePick.oId
                ? {
                    ...o,
                    imageFile: file,
                    imageUrl: null,
                    optionFileContainerId: null
                  }
                : o
            )
          }
        })
      )
      setFilePick(null)
      e.target.value = ''
    },
    [filePick]
  )

  const onDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return
    const { source, destination } = result
    if (source.index === destination.index) return

    if (
      source.droppableId === 'questions-list' &&
      destination.droppableId === 'questions-list'
    ) {
      setQuestions((prev) => reorder(prev, source.index, destination.index))
      return
    }

    if (source.droppableId !== destination.droppableId) return
    const droppableId = destination.droppableId
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== droppableId) return q
        return {
          ...q,
          options: reorder(q.options, source.index, destination.index)
        }
      })
    )
  }, [])

  const updateQuestion = useCallback(
    (qId: string, patch: Partial<QuestionDraft>) => {
      setQuestions((prev) =>
        prev.map((q) => (q.id === qId ? { ...q, ...patch } : q))
      )
    },
    []
  )

  const setAnswerMode = useCallback(
    (qId: string, mode: AnswerMode) => {
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.id !== qId) return q
          const kindPatch = isQuestionnaire
            ? {
                kind: (mode === 'multiple'
                  ? 'multiple'
                  : 'single') as SurveyQuestionKind
              }
            : {}
          if (mode === 'single') {
            const firstCorrect = q.options.findIndex((o) => o.correct)
            const idx = firstCorrect >= 0 ? firstCorrect : 0
            return {
              ...q,
              ...kindPatch,
              answerMode: mode,
              options: q.options.map((o, i) => ({
                ...o,
                correct: i === idx
              }))
            }
          }
          return { ...q, ...kindPatch, answerMode: mode }
        })
      )
    },
    [isQuestionnaire]
  )

  const setSurveyUiType = useCallback((qId: string, uiType: SurveyUiType) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== qId) return q
        const prevKind = draftKind(q)
        if (surveyKindToUiType(prevKind) === uiType) return q
        return { ...q, ...surveyFieldsForUiType(uiType, prevKind) }
      })
    )
  }, [])

  const setScalePreset = useCallback(
    (qId: string, max: 5 | 10) => {
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.id !== qId) return q
          const patch: Partial<QuestionDraft> = {
            scaleMin: 1,
            scaleMax: max
          }
          if (draftKind(q) === 'matrix') {
            patch.matrixCols = buildScaleCols(max)
          }
          return { ...q, ...patch }
        })
      )
    },
    []
  )

  const setOnlyCorrect = useCallback((qId: string, optionId: string) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== qId) return q
        return {
          ...q,
          options: q.options.map((o) => ({
            ...o,
            correct: o.id === optionId
          }))
        }
      })
    )
  }, [])

  const toggleOptionCorrect = useCallback((qId: string, optionId: string) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== qId) return q
        return {
          ...q,
          options: q.options.map((o) =>
            o.id === optionId ? { ...o, correct: !o.correct } : o
          )
        }
      })
    )
  }, [])

  const updateOptionText = useCallback(
    (qId: string, optionId: string, text: string) => {
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.id !== qId) return q
          return {
            ...q,
            options: q.options.map((o) =>
              o.id === optionId ? { ...o, text } : o
            )
          }
        })
      )
    },
    []
  )

  const addOption = useCallback(
    (qId: string) => {
      const q = questionsRef.current.find((x) => x.id === qId)
      if (q && q.options.length >= MAX_ANSWER_OPTIONS) {
        toasts.error(t('modal.test_create.questions.options_limit'))
        return
      }
      setQuestions((prev) =>
        prev.map((item) =>
          item.id === qId
            ? { ...item, options: [...item.options, makeOption()] }
            : item
        )
      )
    },
    [t, toasts]
  )

  const removeOption = useCallback((qId: string, optionId: string) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== qId || q.options.length <= 2) return q
        const next = q.options.filter((o) => o.id !== optionId)
        if (q.answerMode === 'single' && !next.some((o) => o.correct)) {
          next[0] = { ...next[0], correct: true }
        }
        return { ...q, options: next }
      })
    )
  }, [])

  const clearQuestionImage = useCallback((qId: string) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === qId
          ? {
              ...q,
              imageFile: null,
              imageUrl: null,
              questionFileContainerId: null
            }
          : q
      )
    )
  }, [])

  const clearOptionImage = useCallback((qId: string, optionId: string) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id !== qId
          ? q
          : {
              ...q,
              options: q.options.map((o) =>
                o.id === optionId
                  ? {
                      ...o,
                      imageFile: null,
                      imageUrl: null,
                      optionFileContainerId: null
                    }
                  : o
              )
            }
      )
    )
  }, [])

  const removeQuestion = useCallback((qId: string) => {
    setQuestions((prev) => {
      const next = prev.filter((q) => q.id !== qId)
      if (next.length === 0) {
        return [makeQuestion()]
      }
      return next
    })
  }, [])

  const addQuestion = useCallback(() => {
    setQuestions((prev) => [...prev, makeQuestion('single')])
  }, [])

  const saveQuestion = useCallback(
    async (qId: string) => {
      const tid = testItemId?.trim()
      const tok = accessToken?.trim()
      if (!tid || !tok) {
        toasts.error(tq('modal.test_create.questions.save_no_context'))
        return
      }
      const list = questionsRef.current
      const qIndex = list.findIndex((x) => x.id === qId)
      const q = list[qIndex]
      if (!q || qIndex < 0) {
        return
      }
      if (!isQuestionValidForSave(q)) {
        if (!q.text.trim()) {
          toasts.error(t('modal.test_create.questions.question_required'))
          return
        }
        const kind = draftKind(q)
        if (
          kind === 'single' ||
          kind === 'multiple' ||
          kind === 'dropdown'
        ) {
          toasts.error(t('modal.test_create.questions.options_required'))
          return
        }
        if (kind === 'scale') {
          toasts.error(t('modal.test_create.questions.scale_invalid'))
          return
        }
        if (kind === 'matrix') {
          toasts.error(t('modal.test_create.questions.matrix_invalid'))
          return
        }
        toasts.error(t('modal.test_create.questions.question_required'))
        return
      }
      if (!isQuestionnaire && countFilledOptions(q) < 2) {
        toasts.error(t('modal.test_create.questions.options_required'))
        return
      }
      {
        const saveKind = draftKind(q)
        if (
          (saveKind === 'single' ||
            saveKind === 'multiple' ||
            saveKind === 'dropdown') &&
          q.options.length > MAX_ANSWER_OPTIONS
        ) {
          toasts.error(t('modal.test_create.questions.options_limit'))
          return
        }
      }
      setSavingQuestionId(qId)
      try {
        let questionFileContainerId = q.questionFileContainerId ?? ''
        if (q.imageFile) {
          const lr = longreadId?.trim()
          if (!lr) {
            toasts.error(t('modal.test_create.questions.image_no_longread'))
            setSavingQuestionId(null)
            return
          }
          questionFileContainerId = await uploadTestBlockImage(q.imageFile, {
            longreadId: lr,
            localization: i18n.language,
            kind: 'question'
          })
        }

        const optionsForApi = await Promise.all(
          q.options.map(async (o) => {
            const allowOptionImages =
              draftKind(q) === 'single' || draftKind(q) === 'multiple'
            let optionFileContainerId = ''
            if (allowOptionImages) {
              optionFileContainerId = o.optionFileContainerId ?? ''
              if (o.imageFile) {
                const lr = longreadId?.trim()
                if (!lr) {
                  throw new Error(
                    t('modal.test_create.questions.image_no_longread')
                  )
                }
                optionFileContainerId = await uploadTestBlockImage(o.imageFile, {
                  longreadId: lr,
                  localization: i18n.language,
                  kind: 'option'
                })
              }
            }
            return {
              text: o.text,
              correct: isQuestionnaire ? false : o.correct,
              optionFileId: optionFileContainerId,
              optionImageIsNew: allowOptionImages && Boolean(o.imageFile)
            }
          })
        )

        const kind = draftKind(q)
        const payload = isQuestionnaire
          ? buildManagerQuestionnaireQuestionPayload(
              {
                id: q.id,
                text: q.text,
                answerMode: q.answerMode,
                kind,
                options: optionsForApi,
                explanation: '',
                questionFileId: questionFileContainerId,
                questionImageIsNew: Boolean(q.imageFile),
                isRequired: q.isRequired,
                scaleMin: q.scaleMin,
                scaleMax: q.scaleMax,
                scaleMinLabel: q.scaleMinLabel,
                scaleMaxLabel: q.scaleMaxLabel,
                matrixRows: q.matrixRows,
                matrixCols:
                  kind === 'matrix'
                    ? buildScaleCols(q.scaleMax ?? 5)
                    : q.matrixCols
              },
              tid,
              qIndex
            )
          : buildManagerQuestionAddPayload(
              {
                id: q.id,
                text: q.text,
                answerMode: q.answerMode,
                options: optionsForApi,
                explanation: q.explanation,
                questionFileId: questionFileContainerId,
                questionImageIsNew: Boolean(q.imageFile)
              },
              tid,
              qIndex
            )
        const useUpdate = shouldUseManagerQuestionUpdate({
          id: q.id,
          serverQuestionId: q.serverQuestionId
        })
        const raw = useUpdate
          ? await updateManagerQuestion(payload, { accessToken: tok })
          : await addManagerQuestion(payload, { accessToken: tok })
        const serverId =
          parseManagerQuestionAddResponse(raw) ??
          (useUpdate
            ? (q.serverQuestionId?.trim() || q.id.trim() || null)
            : null)

        let fromApi: ReturnType<typeof mapApiQuestionsToReportDrafts>[number] | undefined
        try {
          const listRaw = await fetchManagerQuestionList(tid, {
            accessToken: tok
          })
          const fromList = unwrapManagerQuestionListPayload(listRaw)
          const hydrated = mapApiQuestionsToReportDrafts(fromList)
          const matchKey = serverId?.trim() || q.id
          fromApi = hydrated.find(
            (hq) =>
              (hq.serverQuestionId && hq.serverQuestionId === matchKey) ||
              hq.id === matchKey ||
              hq.id === q.id
          )
        } catch {
          fromApi = undefined
        }

        setQuestions((prev) =>
          prev.map((qq) => {
            if (qq.id !== qId) return qq
            const next: QuestionDraft = { ...qq, saved: true }
            if (serverId) {
              next.serverQuestionId = serverId
              if (qq.id !== serverId) {
                next.id = serverId
              }
            }
            next.questionFileContainerId =
              (questionFileContainerId || qq.questionFileContainerId) ?? null
            next.options = qq.options.map((o, i) => ({
              ...o,
              optionFileContainerId:
                optionsForApi[i]?.optionFileId ??
                o.optionFileContainerId ??
              null
            }))

            if (fromApi) {
              const hydratedQ = fromApi
              if (hydratedQ.imageUrl) {
                next.imageUrl = hydratedQ.imageUrl
                next.imageFile = null
              }
              if (
                hydratedQ.scaleMax === 5 ||
                hydratedQ.scaleMax === 10
              ) {
                next.scaleMax = hydratedQ.scaleMax
                next.scaleMin = hydratedQ.scaleMin ?? 1
                if (draftKind(qq) === 'matrix') {
                  next.matrixCols =
                    hydratedQ.matrixCols && hydratedQ.matrixCols.length > 0
                      ? hydratedQ.matrixCols
                      : buildScaleCols(hydratedQ.scaleMax)
                }
              }
              next.options = next.options.map((o, i) => {
                const apiOpt =
                  hydratedQ.options.find((ao) => ao.id === o.id) ??
                  hydratedQ.options[i]
                if (apiOpt?.imageUrl) {
                  return {
                    ...o,
                    imageUrl: apiOpt.imageUrl,
                    imageFile: null
                  }
                }
                return o
              })
            }

            return next
          })
        )
      } catch (e) {
        const raw =
          e instanceof Error
            ? e.message
            : t('modal.test_create.questions.save_error')
        const message = isAnswerOptionsCountError(raw)
          ? t('modal.test_create.questions.options_limit')
          : raw
        toasts.error(message)
      } finally {
        setSavingQuestionId(null)
      }
    },
    [
      testItemId,
      accessToken,
      longreadId,
      t,
      toasts,
      i18n.language,
      isQuestionnaire
    ]
  )

  const editQuestion = useCallback((qId: string) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === qId ? { ...q, saved: false } : q))
    )
  }, [])

  return (
    <>
      <QuestionsSectionTitle>
        {t('modal.test_create.questions.section_title')}
      </QuestionsSectionTitle>

      <HiddenFileInput
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={onFileChange}
      />

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="questions-list" type={DND_QUESTION_TYPE}>
          {(listProvided) => (
            <QuestionsDroppableList
              ref={listProvided.innerRef}
              {...listProvided.droppableProps}
            >
              {questions.map((q, qIndex) => (
                <Draggable
                  key={q.id}
                  draggableId={`question-${q.id}`}
                  index={qIndex}
                >
                  {(qp) => (
                    <QuestionFormCard
                      ref={qp.innerRef}
                      {...qp.draggableProps}
                      $saved={q.saved}
                    >
                      {q.saved ? (
                        <SavedQuestionBlock
                          q={q}
                          dragHandleProps={
                            (qp.dragHandleProps ?? {}) as Record<string, unknown>
                          }
                          onDelete={() => removeQuestion(q.id)}
                          onEdit={() => editQuestion(q.id)}
                          t={t}
                          isQuestionnaire={isQuestionnaire}
                        />
                      ) : (
                        <>
                          <QuestionCardDragRow>
                            <DragHandle
                              type="button"
                              {...(qp.dragHandleProps ?? {})}
                              aria-label={t(
                                'modal.test_create.questions.drag_handle'
                              )}
                            >
                              <DragDotsIcon />
                            </DragHandle>
                          </QuestionCardDragRow>
                          {isQuestionnaire ? (
                            <FormBlock>
                              <SurveyTypeDropdownWrap>
                                <CommonInput
                                  label={t(
                                    'modal.test_create.questions.type_label'
                                  )}
                                  orientation="column"
                                  type={INPUT_TYPES.dropdown}
                                  options={surveyTypeOptions as any}
                                  value={
                                    (surveyTypeOptions.find(
                                      (o) =>
                                        o.value ===
                                        surveyKindToUiType(draftKind(q))
                                    ) ?? undefined) as any
                                  }
                                  onChange={(item: any) => {
                                    const next = item?.value as
                                      | SurveyUiType
                                      | undefined
                                    if (!next) return
                                    setSurveyUiType(q.id, next)
                                  }}
                                />
                              </SurveyTypeDropdownWrap>
                            </FormBlock>
                          ) : null}
                          <FormBlock>
                            <FieldLabel
                              htmlFor={`question-text-${q.id}`}
                              $required
                            >
                              {isQuestionnaire &&
                              (draftKind(q) === 'info' ||
                                draftKind(q) === 'matrix')
                                ? t('modal.test_create.questions.title_label')
                                : t(
                                    'modal.test_create.questions.question_label'
                                  )}
                            </FieldLabel>
              <QuestionTextRow direction="row" align="flex-end">
                <QuestionInputColumn>
                  <QuestionTextareaWrap>
                    <QuestionTextarea
                      id={`question-text-${q.id}`}
                      value={q.text}
                      maxLength={questionTextMax}
                      onChange={(e) =>
                        updateQuestion(q.id, {
                          text: e.target.value.slice(0, questionTextMax)
                        })
                      }
                      placeholder={
                        isQuestionnaire &&
                        (draftKind(q) === 'info' ||
                          draftKind(q) === 'matrix')
                          ? t(
                              'modal.test_create.questions.title_placeholder'
                            )
                          : t(
                              'modal.test_create.questions.question_placeholder'
                            )
                      }
                    />
                    <CharCounter $footer>
                      {q.text.length}/{questionTextMax}
                    </CharCounter>
                  </QuestionTextareaWrap>
                  {(q.imageFile || q.imageUrl) && (
                    <AttachedImageRow>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <QuestionImagePreview
                          file={q.imageFile}
                          remoteUrl={q.imageUrl}
                        />
                      </div>
                      <IconBtn
                        type="button"
                        aria-label={t(
                          'modal.test_create.questions.remove_image_question'
                        )}
                        onClick={() => clearQuestionImage(q.id)}
                      >
                        <RemoveImageIcon />
                      </IconBtn>
                    </AttachedImageRow>
                  )}
                </QuestionInputColumn>
                <IconBtn
                  type="button"
                  aria-label={t(
                    'modal.test_create.questions.attach_image_question'
                  )}
                  onClick={() =>
                    openFilePicker({ kind: 'question', qId: q.id })
                  }
                >
                  <ImageIcon />
                </IconBtn>
              </QuestionTextRow>
            </FormBlock>

            {(() => {
              const kind = draftKind(q)
              const isChoiceKind =
                kind === 'single' ||
                kind === 'multiple' ||
                kind === 'dropdown'
              const allowOptionImages =
                kind === 'single' || kind === 'multiple'
              const showOptions = !isQuestionnaire || isChoiceKind
              const showAnswerMode =
                !isQuestionnaire ||
                kind === 'single' ||
                kind === 'multiple'
              const showCorrectMarks = !isQuestionnaire
              const showExplanation = !isQuestionnaire
              const showTextLimit =
                isQuestionnaire &&
                (kind === 'text_short' || kind === 'text_long')
              const showScale =
                isQuestionnaire &&
                (kind === 'scale' || kind === 'matrix')
              const showMatrix = isQuestionnaire && kind === 'matrix'
              const scalePreset: 5 | 10 =
                (q.scaleMax ?? 5) >= 10 ? 10 : 5

              return (
                <>
                  {showAnswerMode ? (
                    <FormBlock>
                      <SubTitle>
                        {t('modal.test_create.questions.answer_mode_title')}
                      </SubTitle>
                      <RadioStack>
                        <RadioButton
                          name={`answer-mode-${q.id}`}
                          checked={
                            isQuestionnaire
                              ? kind === 'single'
                              : q.answerMode === 'single'
                          }
                          onChange={() => setAnswerMode(q.id, 'single')}
                        >
                          <span className="radio-text">
                            {isQuestionnaire
                              ? t(
                                  'modal.test_create.questions.survey_single_answer'
                                )
                              : t(
                                  'modal.test_create.questions.single_correct'
                                )}
                          </span>
                        </RadioButton>
                        <RadioButton
                          name={`answer-mode-${q.id}`}
                          checked={
                            isQuestionnaire
                              ? kind === 'multiple'
                              : q.answerMode === 'multiple'
                          }
                          onChange={() => setAnswerMode(q.id, 'multiple')}
                        >
                          <span className="radio-text">
                            {isQuestionnaire
                              ? t(
                                  'modal.test_create.questions.survey_multiple_answers'
                                )
                              : t(
                                  'modal.test_create.questions.multiple_correct'
                                )}
                          </span>
                        </RadioButton>
                      </RadioStack>
                    </FormBlock>
                  ) : null}

                  {showTextLimit ? (
                    <FormBlock>
                      <SubTitle>
                        {t('modal.test_create.questions.text_limit_title')}
                      </SubTitle>
                      <RadioStack>
                        <RadioButton
                          name={`text-limit-${q.id}`}
                          checked={kind === 'text_short'}
                          onChange={() =>
                            updateQuestion(q.id, { kind: 'text_short' })
                          }
                        >
                          <span className="radio-text">
                            {t(
                              'modal.test_create.questions.text_limit_short'
                            )}
                          </span>
                        </RadioButton>
                        <RadioButton
                          name={`text-limit-${q.id}`}
                          checked={kind === 'text_long'}
                          onChange={() =>
                            updateQuestion(q.id, { kind: 'text_long' })
                          }
                        >
                          <span className="radio-text">
                            {t(
                              'modal.test_create.questions.text_limit_long'
                            )}
                          </span>
                        </RadioButton>
                      </RadioStack>
                    </FormBlock>
                  ) : null}

                  {showScale ? (
                    <FormBlock>
                      <SubTitle>
                        {t('modal.test_create.questions.scale_title')}
                      </SubTitle>
                      <RadioStack>
                        <RadioButton
                          name={`scale-preset-${q.id}`}
                          checked={scalePreset === 5}
                          onChange={() => setScalePreset(q.id, 5)}
                        >
                          <span className="radio-text">
                            {t('modal.test_create.questions.scale_1_5')}
                          </span>
                        </RadioButton>
                        <RadioButton
                          name={`scale-preset-${q.id}`}
                          checked={scalePreset === 10}
                          onChange={() => setScalePreset(q.id, 10)}
                        >
                          <span className="radio-text">
                            {t('modal.test_create.questions.scale_1_10')}
                          </span>
                        </RadioButton>
                      </RadioStack>
                    </FormBlock>
                  ) : null}

                  {showMatrix ? (
                    <FormBlock>
                      <SubTitle $required>
                        {t('modal.test_create.questions.matrix_rows_title')}
                      </SubTitle>
                      <OptionsList>
                        {(q.matrixRows ?? ['', '', '']).map(
                          (row, rowIndex) => (
                            <OptionRow key={`${q.id}-mrow-${rowIndex}`}>
                              <OptionFieldColumn>
                                <OptionInput
                                  value={row}
                                  onChange={(e) => {
                                    const next = [...(q.matrixRows ?? [])]
                                    next[rowIndex] = e.target.value
                                    updateQuestion(q.id, {
                                      matrixRows: next
                                    })
                                  }}
                                  placeholder={t(
                                    'modal.test_create.questions.matrix_row_placeholder'
                                  )}
                                />
                              </OptionFieldColumn>
                              <OptionActions>
                                <IconBtn
                                  type="button"
                                  aria-label={t(
                                    'modal.test_create.questions.remove_option'
                                  )}
                                  disabled={
                                    (q.matrixRows ?? []).length <= 1
                                  }
                                  onClick={() => {
                                    const next = (
                                      q.matrixRows ?? []
                                    ).filter((_, i) => i !== rowIndex)
                                    updateQuestion(q.id, {
                                      matrixRows: next.length
                                        ? next
                                        : ['']
                                    })
                                  }}
                                >
                                  <TrashIcon />
                                </IconBtn>
                              </OptionActions>
                            </OptionRow>
                          )
                        )}
                      </OptionsList>
                      <AddTagButton
                        type="button"
                        onClick={() =>
                          updateQuestion(q.id, {
                            matrixRows: [...(q.matrixRows ?? []), '']
                          })
                        }
                      >
                        <PlusIconThin />
                        {t('modal.test_create.questions.matrix_add_row')}
                      </AddTagButton>
                    </FormBlock>
                  ) : null}

                  {showOptions ? (
                    <FormBlock>
                      <SubTitle $required>
                        {t('modal.test_create.questions.options_title')}
                      </SubTitle>
                      {showCorrectMarks ? (
                        <MarkCorrectHintRow direction="row" align="center">
                          {q.answerMode === 'single' ? (
                            <MarkCorrectHintDummy
                              key={q.answerMode}
                              type="radio"
                              name={`mark-correct-hint-${q.id}`}
                              defaultChecked
                              readOnly
                              tabIndex={-1}
                              aria-hidden
                            />
                          ) : (
                            <UiCheckboxWrap>
                              <CommonCheckbox
                                checked
                                disabled
                                onClick={() => {}}
                              />
                            </UiCheckboxWrap>
                          )}
                          <MarkCorrectHintLabel>
                            {t('modal.test_create.questions.mark_correct_hint')}
                          </MarkCorrectHintLabel>
                        </MarkCorrectHintRow>
                      ) : null}
                      <Droppable droppableId={q.id} type={DND_OPTION_TYPE}>
                        {(provided) => (
                          <OptionsList
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                          >
                            {q.options.map((opt, index) => (
                              <Draggable
                                key={opt.id}
                                draggableId={`${q.id}::${opt.id}`}
                                index={index}
                              >
                                {(dragProvided) => (
                                  <OptionRow
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                  >
                                    <DragHandle
                                      type="button"
                                      {...dragProvided.dragHandleProps}
                                      aria-label={t(
                                        'modal.test_create.questions.drag_handle'
                                      )}
                                    >
                                      <DragDotsIcon />
                                    </DragHandle>
                                    {showCorrectMarks ? (
                                      q.answerMode === 'single' ? (
                                        <OptionMark
                                          type="radio"
                                          name={`correct-${q.id}`}
                                          checked={opt.correct}
                                          onChange={() =>
                                            setOnlyCorrect(q.id, opt.id)
                                          }
                                        />
                                      ) : (
                                        <UiCheckboxWrap>
                                          <CommonCheckbox
                                            checked={Boolean(opt.correct)}
                                            onClick={() =>
                                              toggleOptionCorrect(q.id, opt.id)
                                            }
                                          />
                                        </UiCheckboxWrap>
                                      )
                                    ) : null}
                                    <OptionFieldColumn>
                                      <OptionInput
                                        value={opt.text}
                                        onChange={(e) =>
                                          updateOptionText(
                                            q.id,
                                            opt.id,
                                            e.target.value
                                          )
                                        }
                                        placeholder={t(
                                          'modal.test_create.questions.option_placeholder'
                                        )}
                                      />
                                      {(allowOptionImages &&
                                        (opt.imageFile || opt.imageUrl)) && (
                                        <AttachedImageRow>
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <OptionImagePreview
                                              file={opt.imageFile}
                                              remoteUrl={opt.imageUrl}
                                            />
                                          </div>
                                          <IconBtn
                                            type="button"
                                            aria-label={t(
                                              'modal.test_create.questions.remove_image_option'
                                            )}
                                            onClick={() =>
                                              clearOptionImage(q.id, opt.id)
                                            }
                                          >
                                            <RemoveImageIcon />
                                          </IconBtn>
                                        </AttachedImageRow>
                                      )}
                                    </OptionFieldColumn>
                                    <OptionActions>
                                      {allowOptionImages ? (
                                        <IconBtn
                                          type="button"
                                          aria-label={t(
                                            'modal.test_create.questions.attach_image_option'
                                          )}
                                          onClick={() =>
                                            openFilePicker({
                                              kind: 'option',
                                              qId: q.id,
                                              oId: opt.id
                                            })
                                          }
                                        >
                                          <ImageIcon />
                                        </IconBtn>
                                      ) : null}
                                      <IconBtn
                                        type="button"
                                        aria-label={t(
                                          'modal.test_create.questions.remove_option'
                                        )}
                                        onClick={() =>
                                          removeOption(q.id, opt.id)
                                        }
                                        disabled={q.options.length <= 2}
                                      >
                                        <TrashIcon />
                                      </IconBtn>
                                    </OptionActions>
                                  </OptionRow>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </OptionsList>
                        )}
                      </Droppable>
                      <AddTagButton
                        type="button"
                        disabled={q.options.length >= MAX_ANSWER_OPTIONS}
                        onClick={() => addOption(q.id)}
                      >
                        <PlusIconThin />
                        {t('modal.test_create.questions.add_option')}
                      </AddTagButton>
                    </FormBlock>
                  ) : null}

                  {showExplanation ? (
                    <FormBlock>
                      <LabelRow direction="row" align="center">
                        <InlineFieldLabel>
                          {t('modal.test_create.questions.explanation_label')}
                        </InlineFieldLabel>
                        <InfoIcon
                          stc
                          msg={t('modal.test_create.questions.explanation_hint')}
                        />
                      </LabelRow>
                      <ExplanationWrap>
                        <ExplanationTextarea
                          value={q.explanation}
                          maxLength={EXPLANATION_MAX}
                          onChange={(e) =>
                            updateQuestion(q.id, {
                              explanation: e.target.value.slice(
                                0,
                                EXPLANATION_MAX
                              )
                            })
                          }
                          placeholder={t(
                            'modal.test_create.questions.explanation_placeholder'
                          )}
                        />
                        <CharCounter>
                          {q.explanation.length}/{EXPLANATION_MAX}
                        </CharCounter>
                      </ExplanationWrap>
                    </FormBlock>
                  ) : null}
                </>
              )
            })()}

            <FormActions direction="row">
              <BrgButton
                variant={ButtonVariant.Hollow}
                width={132}
                onClick={() => removeQuestion(q.id)}
              >
                {t('modal.test_create.questions.delete_question')}
              </BrgButton>
              {isQuestionValidForSave(q) ? (
                <BrgButton
                  variant={ButtonVariant.Filled}
                  width={180}
                  disabled={savingQuestionId === q.id}
                  onClick={() => void saveQuestion(q.id)}
                >
                  {t('modal.test_create.questions.save_question')}
                </BrgButton>
              ) : null}
            </FormActions>
                        </>
                      )}
                    </QuestionFormCard>
                  )}
                </Draggable>
              ))}
              {listProvided.placeholder}
            </QuestionsDroppableList>
          )}
        </Droppable>
      </DragDropContext>

      <AddTagButton type="button" onClick={addQuestion}>
        <PlusIconThin />
        {t('modal.test_create.questions.add_question')}
      </AddTagButton>
    </>
  )
}

export default TestCreateQuestionsTab

import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { DropResult } from 'react-beautiful-dnd'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import {
  BrgButton,
  ButtonVariant,
  CommonInput,
  INPUT_TYPES,
  useToasts
} from '@brg/ui'
import PlusIconThin from 'shared/icons/PlusIconThin'
import RadioButton from 'shared/components/Radio/RadioButton'
import { CommonCheckbox } from 'shared/components/CommonCheckbox/CommonCheckbox'
import { useTranslation } from 'react-i18next'
import {
  addManagerQuestion,
  buildManagerQuestionAddPayload,
  fetchManagerQuestionList,
  parseManagerQuestionAddResponse,
  shouldUseManagerQuestionUpdate,
  unwrapManagerQuestionListPayload,
  updateManagerQuestion
} from 'shared/api/managerQuestion'
import { buildManagerQuestionnaireQuestionPayload } from 'shared/api/managerQuestionnaireQuestion'
import { mapApiQuestionsToReportDrafts } from './hydrateTestFromApi'
import { uploadTestBlockImage } from 'shared/api/uploadTestBlockImage'
import { resolvedRemoteImageSrc } from 'shared/utils/resolveFilesBurgdevUrl'
import {
  buildScaleCols,
  createEmptyQuestionDraft,
  surveyFieldsForUiType,
  surveyKindToUiType,
  SURVEY_UI_TYPE_OPTIONS,
  type AnswerMode,
  type AnswerOption,
  type ProductKind,
  type QuestionDraft,
  type SurveyQuestionKind,
  type SurveyUiType
} from './questionTypes'
import {
  countFilledOptions,
  isQuestionValidForSave
} from './questionDraftValidation'
import { useFileObjectUrl } from './useFileObjectUrl'
import { InfoIcon } from 'shared/components/InfoIcon/InfoIcon'
import {
  QuestionsSectionTitle,
  QuestionsDroppableList,
  QuestionFormCard,
  QuestionCardDragRow,
  SavedQuestionRoot,
  SavedDesktopHandle,
  SavedBody,
  SavedDesktopKindLabel,
  SavedMobileKindLabel,
  SavedActions,
  SavedActionBtn,
  SavedQuestionTitle,
  ShowFullToggle,
  SavedQuestionImageCenter,
  SavedQuestionImage,
  SavedOptionsStack,
  SavedOptionRow,
  SavedOptionBody,
  SavedOptionText,
  SavedOptionMark,
  SavedExplanationSection,
  SavedExplanationLabel,
  SavedExplanationText,
  FormBlock,
  SurveyTypeDropdownWrap,
  FieldLabel,
  InlineFieldLabel,
  LabelRow,
  QuestionTextRow,
  QuestionInputColumn,
  QuestionTextareaWrap,
  QuestionTextarea,
  CharCounter,
  ImagePreviewBox,
  AttachedImageRow,
  ImagePreviewImg,
  SubTitle,
  MarkCorrectHintRow,
  MarkCorrectHintDummy,
  MarkCorrectHintLabel,
  RadioStack,
  OptionsList,
  OptionRow,
  OptionFieldColumn,
  OptionImagePreviewImg,
  OptionActions,
  DragHandle,
  OptionInput,
  OptionMark,
  IconBtn,
  ExplanationWrap,
  ExplanationTextarea,
  FormActions,
  HiddenFileInput,
  UiCheckboxWrap,
  AddTagButton
} from './questionsTabStyles'

const uid = () =>
  `q-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`

const QUESTION_TEXT_MAX = 100
const QUESTIONNAIRE_TEXT_MAX = 4000
const EXPLANATION_MAX = 500
const MAX_ANSWER_OPTIONS = 10
const DND_QUESTION_TYPE = 'tc-question'
const DND_OPTION_TYPE = 'tc-option'

function isAnswerOptionsCountError(message: string): boolean {
  const lower = message.toLowerCase()
  return (
    lower.includes('answeroptions') ||
    (lower.includes('answer') &&
      lower.includes('option') &&
      lower.includes('10'))
  )
}

const QuestionImagePreview: FC<{
  file: File | null
  remoteUrl?: string | null
}> = ({ file, remoteUrl }) => {
  const blobUrl = useFileObjectUrl(file)
  const src = blobUrl ?? resolvedRemoteImageSrc(remoteUrl)
  if (!src) return null
  return (
    <ImagePreviewBox>
      <ImagePreviewImg src={src} alt="" />
    </ImagePreviewBox>
  )
}

const OptionImagePreview: FC<{
  file: File | null
  remoteUrl?: string | null
}> = ({ file, remoteUrl }) => {
  const blobUrl = useFileObjectUrl(file)
  const src = blobUrl ?? resolvedRemoteImageSrc(remoteUrl)
  if (!src) return null
  return <OptionImagePreviewImg src={src} alt="" />
}

const makeOption = (): AnswerOption => ({
  id: uid(),
  text: '',
  correct: false,
  imageFile: null,
  imageUrl: null,
  optionFileContainerId: null
})

const makeQuestion = (kind: SurveyQuestionKind = 'single'): QuestionDraft =>
  createEmptyQuestionDraft(kind)

function reorder<T>(list: T[], startIndex: number, endIndex: number): T[] {
  const result = [...list]
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)
  return result
}

const RemoveImageIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
  >
    <path
      d="M6 6L18 18M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
)

const ImageIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M21 9C21 10.6569 19.6569 12 18 12C16.3431 12 15 10.6569 15 9C15 7.34315 16.3431 6 18 6C19.6569 6 21 7.34315 21 9ZM19.65 9C19.65 9.91127 18.9113 10.65 18 10.65C17.0887 10.65 16.35 9.91127 16.35 9C16.35 8.08873 17.0887 7.35 18 7.35C18.9113 7.35 19.65 8.08873 19.65 9Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.25 3C1.00736 3 0 4.00736 0 5.25V18.75C0 19.9926 1.00736 21 2.25 21H21.75C22.9926 21 24 19.9926 24 18.75V5.25C24 4.00736 22.9926 3 21.75 3H2.25ZM21.75 4.35H2.25C1.75294 4.35 1.35 4.75294 1.35 5.25V17.7473L7.25779 10.7726C7.84463 10.0797 8.92108 10.1051 9.47465 10.8248L13.1152 15.5577C13.1403 15.5903 13.1869 15.5966 13.2198 15.5718L15.4206 13.911C15.934 13.5236 16.6432 13.5281 17.1517 13.9219L22.65 18.1813V5.25C22.65 4.75294 22.2471 4.35 21.75 4.35ZM2.25 19.65C2.03366 19.65 1.83515 19.5737 1.67993 19.4465L8.28791 11.6451C8.3188 11.6086 8.37545 11.61 8.40459 11.6479L12.0451 16.3808C12.5218 17.0005 13.4089 17.1204 14.033 16.6494L16.2338 14.9886C16.2608 14.9682 16.2982 14.9684 16.3249 14.9892L22.1919 19.5342C22.0614 19.6079 21.9106 19.65 21.75 19.65H2.25Z"
      fill="currentColor"
    />
  </svg>
)

const TrashIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="25"
    viewBox="0 0 20 25"
    fill="none"
    aria-hidden
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.80005 2C5.80005 0.89543 6.69548 0 7.80005 0H11.8C12.9046 0 13.8 0.895431 13.8 2V3C13.8 3.45033 13.6512 3.8659 13.4 4.2002H18.8C19.2418 4.2002 19.6 4.55837 19.6 5.0002V9.0002C19.6 9.44202 19.2418 9.8002 18.8 9.8002H17.6V23.0002C17.6 23.9943 16.7941 24.8002 15.8 24.8002H3.8C2.80589 24.8002 2 23.9943 2 23.0002V9.8002H0.8C0.358173 9.8002 0 9.44202 0 9.0002V5.0002C0 4.55837 0.358172 4.2002 0.8 4.2002H6.20005C5.94888 3.8659 5.80005 3.45033 5.80005 3V2ZM7.80005 1.8H11.8C11.9105 1.8 12 1.88954 12 2V3C12 3.11046 11.9105 3.2 11.8 3.2H7.80005C7.68959 3.2 7.60005 3.11046 7.60005 3V2C7.60005 1.88954 7.68959 1.8 7.80005 1.8ZM1.6 5.8002H18V8.2002H1.6V5.8002ZM16 9.8002V23.2002H3.6V9.8002H16Z"
      fill="currentColor"
    />
  </svg>
)

const EditLineIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.2166 2.43934C17.8023 1.85355 18.7521 1.85355 19.3379 2.43934L21.4237 4.52513C22.0094 5.11091 22.0094 6.06066 21.4237 6.64645L7.33668 20.7334C7.13496 20.9352 6.8799 21.0752 6.60141 21.1371L3.21808 21.8889C2.47245 22.0546 1.82384 21.357 2.04333 20.6254L3.22586 16.6836C3.29719 16.4458 3.42641 16.2295 3.60194 16.054L17.2166 2.43934ZM18.6308 3.14645C18.4355 2.95118 18.1189 2.95118 17.9237 3.14645L15.9843 5.08579L18.7772 7.87868L20.7166 5.93934C20.9118 5.74408 20.9118 5.4275 20.7166 5.23223L18.6308 3.14645ZM18.0701 8.58579L15.2772 5.79289L4.30904 16.7611C4.25053 16.8196 4.20746 16.8917 4.18368 16.9709L3.00115 20.9127L6.38448 20.1609C6.47731 20.1402 6.56233 20.0936 6.62957 20.0263L18.0701 8.58579Z"
      fill="currentColor"
    />
  </svg>
)

const DragDotsIcon: FC = () => (
  <svg width="16" height="20" viewBox="0 0 16 20" fill="none" aria-hidden>
    {[0, 1, 2].map((row) => (
      <g key={row} transform={`translate(0 ${row * 7})`}>
        <circle cx="4" cy="3" r="1.5" fill="currentColor" />
        <circle cx="12" cy="3" r="1.5" fill="currentColor" />
      </g>
    ))}
  </svg>
)

const SavedQuestionImageLarge: FC<{
  file: File | null
  remoteUrl?: string | null
}> = ({ file, remoteUrl }) => {
  const blobUrl = useFileObjectUrl(file)
  const src = blobUrl ?? resolvedRemoteImageSrc(remoteUrl)
  if (!src) return null
  return (
    <SavedQuestionImageCenter>
      <SavedQuestionImage src={src} alt="" />
    </SavedQuestionImageCenter>
  )
}

type SavedQuestionBlockProps = {
  q: QuestionDraft
  dragHandleProps: Record<string, unknown>
  onDelete: () => void
  onEdit: () => void
  t: (key: string) => string
  isQuestionnaire?: boolean
}

const draftKind = (q: QuestionDraft): SurveyQuestionKind =>
  q.kind ?? (q.answerMode === 'multiple' ? 'multiple' : 'single')

const savedKindLabelKey = (kind: SurveyQuestionKind): string => {
  const uiType = surveyKindToUiType(kind)
  if (uiType === 'standard') {
    return 'modal.test_create.questions.saved_kind_standard'
  }
  const option = SURVEY_UI_TYPE_OPTIONS.find((item) => item.uiType === uiType)
  return option?.labelKey ?? 'modal.test_create.questions.saved_kind_standard'
}

const ShowFullChevron: FC = () => (
  <svg
    width="16"
    height="9"
    viewBox="0 0 28 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path d="M14 12.316L25.4804 0.446936C26.0568 -0.148985 26.9913 -0.148977 27.5677 0.446951C28.1441 1.04288 28.1441 2.00906 27.5677 2.60498L15.0437 15.5531C14.4673 16.149 13.5327 16.149 12.9563 15.5531L0.432308 2.60498C-0.144096 2.00906 -0.144102 1.04288 0.432297 0.44695C1.00869 -0.148978 1.94323 -0.148986 2.51963 0.446935L14 12.316Z" />
  </svg>
)

const ExpandableSavedQuestionTitle: FC<{
  text: string
  t: (key: string) => string
}> = ({ text, t }) => {
  const ref = useRef<HTMLDivElement>(null)
  const [collapsed, setCollapsed] = useState(true)
  const [overflows, setOverflows] = useState(false)
  const display = text.trim() ? text : '—'

  useLayoutEffect(() => {
    setCollapsed(true)
  }, [text])

  useLayoutEffect(() => {
    const el = ref.current
    if (!el || !collapsed) {
      return undefined
    }
    const measure = () => {
      setOverflows(el.scrollHeight > el.clientHeight + 1)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') {
      return undefined
    }
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [text, collapsed])

  return (
    <div>
      <SavedQuestionTitle ref={ref} $collapsed={collapsed}>
        {display}
      </SavedQuestionTitle>
      {overflows ? (
        <ShowFullToggle
          type="button"
          $expanded={!collapsed}
          onClick={() => setCollapsed((v) => !v)}
        >
          <span>
            {t(
              collapsed
                ? 'modal.test_create.questions.show_full'
                : 'modal.test_create.questions.hide_full'
            )}
          </span>
          <ShowFullChevron />
        </ShowFullToggle>
      ) : null}
    </div>
  )
}

const SavedQuestionBlock: FC<SavedQuestionBlockProps> = ({
  q,
  dragHandleProps,
  onDelete,
  onEdit,
  t,
  isQuestionnaire = false
}) => {
  const kind = draftKind(q)
  const kindLabel = t(savedKindLabelKey(kind))
  const showChoiceOptions =
    kind === 'single' || kind === 'multiple' || kind === 'dropdown'
  const showScaleSummary = kind === 'scale'
  const showMatrixSummary = kind === 'matrix'

  return (
    <SavedQuestionRoot>
      <SavedDesktopHandle>
        <DragHandle
          type="button"
          {...dragHandleProps}
          aria-label={t('modal.test_create.questions.drag_handle')}
        >
          <DragDotsIcon />
        </DragHandle>
      </SavedDesktopHandle>

      <SavedMobileKindLabel>{kindLabel}</SavedMobileKindLabel>

      <SavedBody>
        <SavedDesktopKindLabel>{kindLabel}</SavedDesktopKindLabel>

        <ExpandableSavedQuestionTitle text={q.text} t={t} />

        <SavedQuestionImageLarge file={q.imageFile} remoteUrl={q.imageUrl} />

        {showChoiceOptions ? (
          <SavedOptionsStack>
            {q.options.map((opt) => (
              <SavedOptionRow key={opt.id}>
                {!isQuestionnaire &&
                  (q.answerMode === 'single' ? (
                    <SavedOptionMark
                      type="radio"
                      name={`saved-correct-${q.id}`}
                      checked={opt.correct}
                      readOnly
                      tabIndex={-1}
                    />
                  ) : (
                    <UiCheckboxWrap>
                      <CommonCheckbox
                        checked={Boolean(opt.correct)}
                        disabled
                        onClick={() => {}}
                      />
                    </UiCheckboxWrap>
                  ))}
                <SavedOptionBody>
                  <SavedOptionText>
                    {opt.text.trim() ? opt.text : '—'}
                  </SavedOptionText>
                  {(kind === 'single' || kind === 'multiple') &&
                  (opt.imageFile || opt.imageUrl) ? (
                    <OptionImagePreview
                      file={opt.imageFile}
                      remoteUrl={opt.imageUrl}
                    />
                  ) : null}
                </SavedOptionBody>
              </SavedOptionRow>
            ))}
          </SavedOptionsStack>
        ) : null}

        {showScaleSummary ? (
          <SavedExplanationSection>
            <SavedExplanationText>
              {t('modal.test_create.questions.scale_summary')
                .replace('{{min}}', String(q.scaleMin ?? 1))
                .replace('{{max}}', String(q.scaleMax ?? 5))}
            </SavedExplanationText>
          </SavedExplanationSection>
        ) : null}

        {showMatrixSummary ? (
          <SavedOptionsStack>
            {(q.matrixRows ?? [])
              .filter((r) => r.trim())
              .map((row, i) => (
                <SavedOptionRow key={`${q.id}-row-${i}`}>
                  <SavedOptionBody>
                    <SavedOptionText>{row}</SavedOptionText>
                  </SavedOptionBody>
                </SavedOptionRow>
              ))}
          </SavedOptionsStack>
        ) : null}

        {!isQuestionnaire && q.explanation.trim() !== '' && (
          <SavedExplanationSection>
            <SavedExplanationLabel>
              {t('modal.test_create.questions.explanation_label')}
            </SavedExplanationLabel>
            <SavedExplanationText>{q.explanation}</SavedExplanationText>
          </SavedExplanationSection>
        )}
      </SavedBody>

      <SavedActions>
        <SavedActionBtn
          type="button"
          aria-label={t('modal.test_create.questions.edit_question')}
          onMouseDown={(e) => {
            e.stopPropagation()
          }}
          onClick={(e) => {
            e.stopPropagation()
            onEdit()
          }}
        >
          <EditLineIcon />
        </SavedActionBtn>
        <SavedActionBtn
          type="button"
          aria-label={t('modal.test_create.questions.delete_question')}
          onMouseDown={(e) => {
            e.stopPropagation()
          }}
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
        >
          <TrashIcon />
        </SavedActionBtn>
      </SavedActions>
    </SavedQuestionRoot>
  )
}

type FilePick =
  | { kind: 'question'; qId: string }
  | { kind: 'option'; qId: string; oId: string }

const TestCreateQuestionsTab: FC<{
  productKind?: ProductKind
  testItemId?: string | null
  accessToken?: string | null
  longreadId?: string | null
  onQuestionsChange?: (questions: QuestionDraft[]) => void
  initialQuestions?: QuestionDraft[] | null
}> = ({
  productKind = 'test',
  testItemId,
  accessToken,
  longreadId,
  onQuestionsChange,
  initialQuestions
}) => {
  const isQuestionnaire = productKind === 'questionnaire'
  const questionTextMax = isQuestionnaire
    ? QUESTIONNAIRE_TEXT_MAX
    : QUESTION_TEXT_MAX
  const { t, i18n } = useTranslation()
  const toasts = useToasts()
  const tq = useCallback(
    (key: string) => {
      if (isQuestionnaire) {
        const surveyKey = `${key}_survey`
        if (i18n.exists(surveyKey)) {
          return t(surveyKey)
        }
      }
      return t(key)
    },
    [isQuestionnaire, i18n, t]
  )
  const [questions, setQuestions] = useState<QuestionDraft[]>(() => {
    if (initialQuestions && initialQuestions.length > 0) {
      return initialQuestions
    }
    return [makeQuestion()]
  })
  const questionsRef = useRef(questions)
  questionsRef.current = questions

  useEffect(() => {
    onQuestionsChange?.(questions)
  }, [questions, onQuestionsChange])

  const [savingQuestionId, setSavingQuestionId] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const [filePick, setFilePick] = useState<FilePick | null>(null)

  const surveyTypeOptions = useMemo(
    () =>
      SURVEY_UI_TYPE_OPTIONS.map((o) => ({
        value: o.uiType,
        label: t(o.labelKey)
      })),
    [t]
  )

  const openFilePicker = useCallback((pick: FilePick) => {
    setFilePick(pick)
    requestAnimationFrame(() => fileRef.current?.click())
  }, [])

  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file || !filePick) {
        e.target.value = ''
        return
      }
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.id !== filePick.qId) return q
          if (filePick.kind === 'question') {
            return {
              ...q,
              imageFile: file,
              imageUrl: null,
              questionFileContainerId: null
            }
          }
          return {
            ...q,
            options: q.options.map((o) =>
              o.id === filePick.oId
                ? {
                    ...o,
                    imageFile: file,
                    imageUrl: null,
                    optionFileContainerId: null
                  }
                : o
            )
          }
        })
      )
      setFilePick(null)
      e.target.value = ''
    },
    [filePick]
  )

  const onDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return
    const { source, destination } = result
    if (source.index === destination.index) return

    if (
      source.droppableId === 'questions-list' &&
      destination.droppableId === 'questions-list'
    ) {
      setQuestions((prev) => reorder(prev, source.index, destination.index))
      return
    }

    if (source.droppableId !== destination.droppableId) return
    const droppableId = destination.droppableId
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== droppableId) return q
        return {
          ...q,
          options: reorder(q.options, source.index, destination.index)
        }
      })
    )
  }, [])

  const updateQuestion = useCallback(
    (qId: string, patch: Partial<QuestionDraft>) => {
      setQuestions((prev) =>
        prev.map((q) => (q.id === qId ? { ...q, ...patch } : q))
      )
    },
    []
  )

  const setAnswerMode = useCallback(
    (qId: string, mode: AnswerMode) => {
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.id !== qId) return q
          const kindPatch = isQuestionnaire
            ? {
                kind: (mode === 'multiple'
                  ? 'multiple'
                  : 'single') as SurveyQuestionKind
              }
            : {}
          if (mode === 'single') {
            const firstCorrect = q.options.findIndex((o) => o.correct)
            const idx = firstCorrect >= 0 ? firstCorrect : 0
            return {
              ...q,
              ...kindPatch,
              answerMode: mode,
              options: q.options.map((o, i) => ({
                ...o,
                correct: i === idx
              }))
            }
          }
          return { ...q, ...kindPatch, answerMode: mode }
        })
      )
    },
    [isQuestionnaire]
  )

  const setSurveyUiType = useCallback((qId: string, uiType: SurveyUiType) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== qId) return q
        const prevKind = draftKind(q)
        if (surveyKindToUiType(prevKind) === uiType) return q
        return { ...q, ...surveyFieldsForUiType(uiType, prevKind) }
      })
    )
  }, [])

  const setScalePreset = useCallback(
    (qId: string, max: 5 | 10) => {
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.id !== qId) return q
          const patch: Partial<QuestionDraft> = {
            scaleMin: 1,
            scaleMax: max
          }
          if (draftKind(q) === 'matrix') {
            patch.matrixCols = buildScaleCols(max)
          }
          return { ...q, ...patch }
        })
      )
    },
    []
  )

  const setOnlyCorrect = useCallback((qId: string, optionId: string) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== qId) return q
        return {
          ...q,
          options: q.options.map((o) => ({
            ...o,
            correct: o.id === optionId
          }))
        }
      })
    )
  }, [])

  const toggleOptionCorrect = useCallback((qId: string, optionId: string) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== qId) return q
        return {
          ...q,
          options: q.options.map((o) =>
            o.id === optionId ? { ...o, correct: !o.correct } : o
          )
        }
      })
    )
  }, [])

  const updateOptionText = useCallback(
    (qId: string, optionId: string, text: string) => {
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.id !== qId) return q
          return {
            ...q,
            options: q.options.map((o) =>
              o.id === optionId ? { ...o, text } : o
            )
          }
        })
      )
    },
    []
  )

  const addOption = useCallback(
    (qId: string) => {
      const q = questionsRef.current.find((x) => x.id === qId)
      if (q && q.options.length >= MAX_ANSWER_OPTIONS) {
        toasts.error(t('modal.test_create.questions.options_limit'))
        return
      }
      setQuestions((prev) =>
        prev.map((item) =>
          item.id === qId
            ? { ...item, options: [...item.options, makeOption()] }
            : item
        )
      )
    },
    [t, toasts]
  )

  const removeOption = useCallback((qId: string, optionId: string) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== qId || q.options.length <= 2) return q
        const next = q.options.filter((o) => o.id !== optionId)
        if (q.answerMode === 'single' && !next.some((o) => o.correct)) {
          next[0] = { ...next[0], correct: true }
        }
        return { ...q, options: next }
      })
    )
  }, [])

  const clearQuestionImage = useCallback((qId: string) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === qId
          ? {
              ...q,
              imageFile: null,
              imageUrl: null,
              questionFileContainerId: null
            }
          : q
      )
    )
  }, [])

  const clearOptionImage = useCallback((qId: string, optionId: string) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id !== qId
          ? q
          : {
              ...q,
              options: q.options.map((o) =>
                o.id === optionId
                  ? {
                      ...o,
                      imageFile: null,
                      imageUrl: null,
                      optionFileContainerId: null
                    }
                  : o
              )
            }
      )
    )
  }, [])

  const removeQuestion = useCallback((qId: string) => {
    setQuestions((prev) => {
      const next = prev.filter((q) => q.id !== qId)
      if (next.length === 0) {
        return [makeQuestion()]
      }
      return next
    })
  }, [])

  const addQuestion = useCallback(() => {
    setQuestions((prev) => [...prev, makeQuestion('single')])
  }, [])

  const saveQuestion = useCallback(
    async (qId: string) => {
      const tid = testItemId?.trim()
      const tok = accessToken?.trim()
      if (!tid || !tok) {
        toasts.error(tq('modal.test_create.questions.save_no_context'))
        return
      }
      const list = questionsRef.current
      const qIndex = list.findIndex((x) => x.id === qId)
      const q = list[qIndex]
      if (!q || qIndex < 0) {
        return
      }
      if (!isQuestionValidForSave(q)) {
        if (!q.text.trim()) {
          toasts.error(t('modal.test_create.questions.question_required'))
          return
        }
        const kind = draftKind(q)
        if (
          kind === 'single' ||
          kind === 'multiple' ||
          kind === 'dropdown'
        ) {
          toasts.error(t('modal.test_create.questions.options_required'))
          return
        }
        if (kind === 'scale') {
          toasts.error(t('modal.test_create.questions.scale_invalid'))
          return
        }
        if (kind === 'matrix') {
          toasts.error(t('modal.test_create.questions.matrix_invalid'))
          return
        }
        toasts.error(t('modal.test_create.questions.question_required'))
        return
      }
      if (!isQuestionnaire && countFilledOptions(q) < 2) {
        toasts.error(t('modal.test_create.questions.options_required'))
        return
      }
      {
        const saveKind = draftKind(q)
        if (
          (saveKind === 'single' ||
            saveKind === 'multiple' ||
            saveKind === 'dropdown') &&
          q.options.length > MAX_ANSWER_OPTIONS
        ) {
          toasts.error(t('modal.test_create.questions.options_limit'))
          return
        }
      }
      setSavingQuestionId(qId)
      try {
        let questionFileContainerId = q.questionFileContainerId ?? ''
        if (q.imageFile) {
          const lr = longreadId?.trim()
          if (!lr) {
            toasts.error(t('modal.test_create.questions.image_no_longread'))
            setSavingQuestionId(null)
            return
          }
          questionFileContainerId = await uploadTestBlockImage(q.imageFile, {
            longreadId: lr,
            localization: i18n.language,
            kind: 'question'
          })
        }

        const optionsForApi = await Promise.all(
          q.options.map(async (o) => {
            const allowOptionImages =
              draftKind(q) === 'single' || draftKind(q) === 'multiple'
            let optionFileContainerId = ''
            if (allowOptionImages) {
              optionFileContainerId = o.optionFileContainerId ?? ''
              if (o.imageFile) {
                const lr = longreadId?.trim()
                if (!lr) {
                  throw new Error(
                    t('modal.test_create.questions.image_no_longread')
                  )
                }
                optionFileContainerId = await uploadTestBlockImage(o.imageFile, {
                  longreadId: lr,
                  localization: i18n.language,
                  kind: 'option'
                })
              }
            }
            return {
              text: o.text,
              correct: isQuestionnaire ? false : o.correct,
              optionFileId: optionFileContainerId,
              optionImageIsNew: allowOptionImages && Boolean(o.imageFile)
            }
          })
        )

        const kind = draftKind(q)
        const payload = isQuestionnaire
          ? buildManagerQuestionnaireQuestionPayload(
              {
                id: q.id,
                text: q.text,
                answerMode: q.answerMode,
                kind,
                options: optionsForApi,
                explanation: '',
                questionFileId: questionFileContainerId,
                questionImageIsNew: Boolean(q.imageFile),
                isRequired: q.isRequired,
                scaleMin: q.scaleMin,
                scaleMax: q.scaleMax,
                scaleMinLabel: q.scaleMinLabel,
                scaleMaxLabel: q.scaleMaxLabel,
                matrixRows: q.matrixRows,
                matrixCols:
                  kind === 'matrix'
                    ? buildScaleCols(q.scaleMax ?? 5)
                    : q.matrixCols
              },
              tid,
              qIndex
            )
          : buildManagerQuestionAddPayload(
              {
                id: q.id,
                text: q.text,
                answerMode: q.answerMode,
                options: optionsForApi,
                explanation: q.explanation,
                questionFileId: questionFileContainerId,
                questionImageIsNew: Boolean(q.imageFile)
              },
              tid,
              qIndex
            )
        const useUpdate = shouldUseManagerQuestionUpdate({
          id: q.id,
          serverQuestionId: q.serverQuestionId
        })
        const raw = useUpdate
          ? await updateManagerQuestion(payload, { accessToken: tok })
          : await addManagerQuestion(payload, { accessToken: tok })
        const serverId =
          parseManagerQuestionAddResponse(raw) ??
          (useUpdate
            ? (q.serverQuestionId?.trim() || q.id.trim() || null)
            : null)

        let fromApi: ReturnType<typeof mapApiQuestionsToReportDrafts>[number] | undefined
        try {
          const listRaw = await fetchManagerQuestionList(tid, {
            accessToken: tok
          })
          const fromList = unwrapManagerQuestionListPayload(listRaw)
          const hydrated = mapApiQuestionsToReportDrafts(fromList)
          const matchKey = serverId?.trim() || q.id
          fromApi = hydrated.find(
            (hq) =>
              (hq.serverQuestionId && hq.serverQuestionId === matchKey) ||
              hq.id === matchKey ||
              hq.id === q.id
          )
        } catch {
          fromApi = undefined
        }

        setQuestions((prev) =>
          prev.map((qq) => {
            if (qq.id !== qId) return qq
            const next: QuestionDraft = { ...qq, saved: true }
            if (serverId) {
              next.serverQuestionId = serverId
              if (qq.id !== serverId) {
                next.id = serverId
              }
            }
            next.questionFileContainerId =
              (questionFileContainerId || qq.questionFileContainerId) ?? null
            next.options = qq.options.map((o, i) => ({
              ...o,
              optionFileContainerId:
                optionsForApi[i]?.optionFileId ??
                o.optionFileContainerId ??
              null
            }))

            if (fromApi) {
              const hydratedQ = fromApi
              if (hydratedQ.imageUrl) {
                next.imageUrl = hydratedQ.imageUrl
                next.imageFile = null
              }
              if (
                hydratedQ.scaleMax === 5 ||
                hydratedQ.scaleMax === 10
              ) {
                next.scaleMax = hydratedQ.scaleMax
                next.scaleMin = hydratedQ.scaleMin ?? 1
                if (draftKind(qq) === 'matrix') {
                  next.matrixCols =
                    hydratedQ.matrixCols && hydratedQ.matrixCols.length > 0
                      ? hydratedQ.matrixCols
                      : buildScaleCols(hydratedQ.scaleMax)
                }
              }
              next.options = next.options.map((o, i) => {
                const apiOpt =
                  hydratedQ.options.find((ao) => ao.id === o.id) ??
                  hydratedQ.options[i]
                if (apiOpt?.imageUrl) {
                  return {
                    ...o,
                    imageUrl: apiOpt.imageUrl,
                    imageFile: null
                  }
                }
                return o
              })
            }

            return next
          })
        )
      } catch (e) {
        const raw =
          e instanceof Error
            ? e.message
            : t('modal.test_create.questions.save_error')
        const message = isAnswerOptionsCountError(raw)
          ? t('modal.test_create.questions.options_limit')
          : raw
        toasts.error(message)
      } finally {
        setSavingQuestionId(null)
      }
    },
    [
      testItemId,
      accessToken,
      longreadId,
      t,
      toasts,
      i18n.language,
      isQuestionnaire
    ]
  )

  const editQuestion = useCallback((qId: string) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === qId ? { ...q, saved: false } : q))
    )
  }, [])

  return (
    <>
      <QuestionsSectionTitle>
        {t('modal.test_create.questions.section_title')}
      </QuestionsSectionTitle>

      <HiddenFileInput
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={onFileChange}
      />

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="questions-list" type={DND_QUESTION_TYPE}>
          {(listProvided) => (
            <QuestionsDroppableList
              ref={listProvided.innerRef}
              {...listProvided.droppableProps}
            >
              {questions.map((q, qIndex) => (
                <Draggable
                  key={q.id}
                  draggableId={`question-${q.id}`}
                  index={qIndex}
                >
                  {(qp) => (
                    <QuestionFormCard
                      ref={qp.innerRef}
                      {...qp.draggableProps}
                      $saved={q.saved}
                    >
                      {q.saved ? (
                        <SavedQuestionBlock
                          q={q}
                          dragHandleProps={
                            (qp.dragHandleProps ?? {}) as Record<string, unknown>
                          }
                          onDelete={() => removeQuestion(q.id)}
                          onEdit={() => editQuestion(q.id)}
                          t={t}
                          isQuestionnaire={isQuestionnaire}
                        />
                      ) : (
                        <>
                          <QuestionCardDragRow>
                            <DragHandle
                              type="button"
                              {...(qp.dragHandleProps ?? {})}
                              aria-label={t(
                                'modal.test_create.questions.drag_handle'
                              )}
                            >
                              <DragDotsIcon />
                            </DragHandle>
                          </QuestionCardDragRow>
                          {isQuestionnaire ? (
                            <FormBlock>
                              <SurveyTypeDropdownWrap>
                                <CommonInput
                                  label={t(
                                    'modal.test_create.questions.type_label'
                                  )}
                                  orientation="column"
                                  type={INPUT_TYPES.dropdown}
                                  options={surveyTypeOptions as any}
                                  value={
                                    (surveyTypeOptions.find(
                                      (o) =>
                                        o.value ===
                                        surveyKindToUiType(draftKind(q))
                                    ) ?? undefined) as any
                                  }
                                  onChange={(item: any) => {
                                    const next = item?.value as
                                      | SurveyUiType
                                      | undefined
                                    if (!next) return
                                    setSurveyUiType(q.id, next)
                                  }}
                                />
                              </SurveyTypeDropdownWrap>
                            </FormBlock>
                          ) : null}
                          <FormBlock>
                            <FieldLabel
                              htmlFor={`question-text-${q.id}`}
                              $required
                            >
                              {isQuestionnaire &&
                              (draftKind(q) === 'info' ||
                                draftKind(q) === 'matrix')
                                ? t('modal.test_create.questions.title_label')
                                : t(
                                    'modal.test_create.questions.question_label'
                                  )}
                            </FieldLabel>
              <QuestionTextRow direction="row" align="flex-end">
                <QuestionInputColumn>
                  <QuestionTextareaWrap>
                    <QuestionTextarea
                      id={`question-text-${q.id}`}
                      value={q.text}
                      maxLength={questionTextMax}
                      onChange={(e) =>
                        updateQuestion(q.id, {
                          text: e.target.value.slice(0, questionTextMax)
                        })
                      }
                      placeholder={
                        isQuestionnaire &&
                        (draftKind(q) === 'info' ||
                          draftKind(q) === 'matrix')
                          ? t(
                              'modal.test_create.questions.title_placeholder'
                            )
                          : t(
                              'modal.test_create.questions.question_placeholder'
                            )
                      }
                    />
                    <CharCounter $footer>
                      {q.text.length}/{questionTextMax}
                    </CharCounter>
                  </QuestionTextareaWrap>
                  {(q.imageFile || q.imageUrl) && (
                    <AttachedImageRow>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <QuestionImagePreview
                          file={q.imageFile}
                          remoteUrl={q.imageUrl}
                        />
                      </div>
                      <IconBtn
                        type="button"
                        aria-label={t(
                          'modal.test_create.questions.remove_image_question'
                        )}
                        onClick={() => clearQuestionImage(q.id)}
                      >
                        <RemoveImageIcon />
                      </IconBtn>
                    </AttachedImageRow>
                  )}
                </QuestionInputColumn>
                <IconBtn
                  type="button"
                  aria-label={t(
                    'modal.test_create.questions.attach_image_question'
                  )}
                  onClick={() =>
                    openFilePicker({ kind: 'question', qId: q.id })
                  }
                >
                  <ImageIcon />
                </IconBtn>
              </QuestionTextRow>
            </FormBlock>

            {(() => {
              const kind = draftKind(q)
              const isChoiceKind =
                kind === 'single' ||
                kind === 'multiple' ||
                kind === 'dropdown'
              const allowOptionImages =
                kind === 'single' || kind === 'multiple'
              const showOptions = !isQuestionnaire || isChoiceKind
              const showAnswerMode =
                !isQuestionnaire ||
                kind === 'single' ||
                kind === 'multiple'
              const showCorrectMarks = !isQuestionnaire
              const showExplanation = !isQuestionnaire
              const showTextLimit =
                isQuestionnaire &&
                (kind === 'text_short' || kind === 'text_long')
              const showScale =
                isQuestionnaire &&
                (kind === 'scale' || kind === 'matrix')
              const showMatrix = isQuestionnaire && kind === 'matrix'
              const scalePreset: 5 | 10 =
                (q.scaleMax ?? 5) >= 10 ? 10 : 5

              return (
                <>
                  {showAnswerMode ? (
                    <FormBlock>
                      <SubTitle>
                        {t('modal.test_create.questions.answer_mode_title')}
                      </SubTitle>
                      <RadioStack>
                        <RadioButton
                          name={`answer-mode-${q.id}`}
                          checked={
                            isQuestionnaire
                              ? kind === 'single'
                              : q.answerMode === 'single'
                          }
                          onChange={() => setAnswerMode(q.id, 'single')}
                        >
                          <span className="radio-text">
                            {isQuestionnaire
                              ? t(
                                  'modal.test_create.questions.survey_single_answer'
                                )
                              : t(
                                  'modal.test_create.questions.single_correct'
                                )}
                          </span>
                        </RadioButton>
                        <RadioButton
                          name={`answer-mode-${q.id}`}
                          checked={
                            isQuestionnaire
                              ? kind === 'multiple'
                              : q.answerMode === 'multiple'
                          }
                          onChange={() => setAnswerMode(q.id, 'multiple')}
                        >
                          <span className="radio-text">
                            {isQuestionnaire
                              ? t(
                                  'modal.test_create.questions.survey_multiple_answers'
                                )
                              : t(
                                  'modal.test_create.questions.multiple_correct'
                                )}
                          </span>
                        </RadioButton>
                      </RadioStack>
                    </FormBlock>
                  ) : null}

                  {showTextLimit ? (
                    <FormBlock>
                      <SubTitle>
                        {t('modal.test_create.questions.text_limit_title')}
                      </SubTitle>
                      <RadioStack>
                        <RadioButton
                          name={`text-limit-${q.id}`}
                          checked={kind === 'text_short'}
                          onChange={() =>
                            updateQuestion(q.id, { kind: 'text_short' })
                          }
                        >
                          <span className="radio-text">
                            {t(
                              'modal.test_create.questions.text_limit_short'
                            )}
                          </span>
                        </RadioButton>
                        <RadioButton
                          name={`text-limit-${q.id}`}
                          checked={kind === 'text_long'}
                          onChange={() =>
                            updateQuestion(q.id, { kind: 'text_long' })
                          }
                        >
                          <span className="radio-text">
                            {t(
                              'modal.test_create.questions.text_limit_long'
                            )}
                          </span>
                        </RadioButton>
                      </RadioStack>
                    </FormBlock>
                  ) : null}

                  {showScale ? (
                    <FormBlock>
                      <SubTitle>
                        {t('modal.test_create.questions.scale_title')}
                      </SubTitle>
                      <RadioStack>
                        <RadioButton
                          name={`scale-preset-${q.id}`}
                          checked={scalePreset === 5}
                          onChange={() => setScalePreset(q.id, 5)}
                        >
                          <span className="radio-text">
                            {t('modal.test_create.questions.scale_1_5')}
                          </span>
                        </RadioButton>
                        <RadioButton
                          name={`scale-preset-${q.id}`}
                          checked={scalePreset === 10}
                          onChange={() => setScalePreset(q.id, 10)}
                        >
                          <span className="radio-text">
                            {t('modal.test_create.questions.scale_1_10')}
                          </span>
                        </RadioButton>
                      </RadioStack>
                    </FormBlock>
                  ) : null}

                  {showMatrix ? (
                    <FormBlock>
                      <SubTitle $required>
                        {t('modal.test_create.questions.matrix_rows_title')}
                      </SubTitle>
                      <OptionsList>
                        {(q.matrixRows ?? ['', '', '']).map(
                          (row, rowIndex) => (
                            <OptionRow key={`${q.id}-mrow-${rowIndex}`}>
                              <OptionFieldColumn>
                                <OptionInput
                                  value={row}
                                  onChange={(e) => {
                                    const next = [...(q.matrixRows ?? [])]
                                    next[rowIndex] = e.target.value
                                    updateQuestion(q.id, {
                                      matrixRows: next
                                    })
                                  }}
                                  placeholder={t(
                                    'modal.test_create.questions.matrix_row_placeholder'
                                  )}
                                />
                              </OptionFieldColumn>
                              <OptionActions>
                                <IconBtn
                                  type="button"
                                  aria-label={t(
                                    'modal.test_create.questions.remove_option'
                                  )}
                                  disabled={
                                    (q.matrixRows ?? []).length <= 1
                                  }
                                  onClick={() => {
                                    const next = (
                                      q.matrixRows ?? []
                                    ).filter((_, i) => i !== rowIndex)
                                    updateQuestion(q.id, {
                                      matrixRows: next.length
                                        ? next
                                        : ['']
                                    })
                                  }}
                                >
                                  <TrashIcon />
                                </IconBtn>
                              </OptionActions>
                            </OptionRow>
                          )
                        )}
                      </OptionsList>
                      <AddTagButton
                        type="button"
                        onClick={() =>
                          updateQuestion(q.id, {
                            matrixRows: [...(q.matrixRows ?? []), '']
                          })
                        }
                      >
                        <PlusIconThin />
                        {t('modal.test_create.questions.matrix_add_row')}
                      </AddTagButton>
                    </FormBlock>
                  ) : null}

                  {showOptions ? (
                    <FormBlock>
                      <SubTitle $required>
                        {t('modal.test_create.questions.options_title')}
                      </SubTitle>
                      {showCorrectMarks ? (
                        <MarkCorrectHintRow direction="row" align="center">
                          {q.answerMode === 'single' ? (
                            <MarkCorrectHintDummy
                              key={q.answerMode}
                              type="radio"
                              name={`mark-correct-hint-${q.id}`}
                              defaultChecked
                              readOnly
                              tabIndex={-1}
                              aria-hidden
                            />
                          ) : (
                            <UiCheckboxWrap>
                              <CommonCheckbox
                                checked
                                disabled
                                onClick={() => {}}
                              />
                            </UiCheckboxWrap>
                          )}
                          <MarkCorrectHintLabel>
                            {t('modal.test_create.questions.mark_correct_hint')}
                          </MarkCorrectHintLabel>
                        </MarkCorrectHintRow>
                      ) : null}
                      <Droppable droppableId={q.id} type={DND_OPTION_TYPE}>
                        {(provided) => (
                          <OptionsList
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                          >
                            {q.options.map((opt, index) => (
                              <Draggable
                                key={opt.id}
                                draggableId={`${q.id}::${opt.id}`}
                                index={index}
                              >
                                {(dragProvided) => (
                                  <OptionRow
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                  >
                                    <DragHandle
                                      type="button"
                                      {...dragProvided.dragHandleProps}
                                      aria-label={t(
                                        'modal.test_create.questions.drag_handle'
                                      )}
                                    >
                                      <DragDotsIcon />
                                    </DragHandle>
                                    {showCorrectMarks ? (
                                      q.answerMode === 'single' ? (
                                        <OptionMark
                                          type="radio"
                                          name={`correct-${q.id}`}
                                          checked={opt.correct}
                                          onChange={() =>
                                            setOnlyCorrect(q.id, opt.id)
                                          }
                                        />
                                      ) : (
                                        <UiCheckboxWrap>
                                          <CommonCheckbox
                                            checked={Boolean(opt.correct)}
                                            onClick={() =>
                                              toggleOptionCorrect(q.id, opt.id)
                                            }
                                          />
                                        </UiCheckboxWrap>
                                      )
                                    ) : null}
                                    <OptionFieldColumn>
                                      <OptionInput
                                        value={opt.text}
                                        onChange={(e) =>
                                          updateOptionText(
                                            q.id,
                                            opt.id,
                                            e.target.value
                                          )
                                        }
                                        placeholder={t(
                                          'modal.test_create.questions.option_placeholder'
                                        )}
                                      />
                                      {(allowOptionImages &&
                                        (opt.imageFile || opt.imageUrl)) && (
                                        <AttachedImageRow>
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <OptionImagePreview
                                              file={opt.imageFile}
                                              remoteUrl={opt.imageUrl}
                                            />
                                          </div>
                                          <IconBtn
                                            type="button"
                                            aria-label={t(
                                              'modal.test_create.questions.remove_image_option'
                                            )}
                                            onClick={() =>
                                              clearOptionImage(q.id, opt.id)
                                            }
                                          >
                                            <RemoveImageIcon />
                                          </IconBtn>
                                        </AttachedImageRow>
                                      )}
                                    </OptionFieldColumn>
                                    <OptionActions>
                                      {allowOptionImages ? (
                                        <IconBtn
                                          type="button"
                                          aria-label={t(
                                            'modal.test_create.questions.attach_image_option'
                                          )}
                                          onClick={() =>
                                            openFilePicker({
                                              kind: 'option',
                                              qId: q.id,
                                              oId: opt.id
                                            })
                                          }
                                        >
                                          <ImageIcon />
                                        </IconBtn>
                                      ) : null}
                                      <IconBtn
                                        type="button"
                                        aria-label={t(
                                          'modal.test_create.questions.remove_option'
                                        )}
                                        onClick={() =>
                                          removeOption(q.id, opt.id)
                                        }
                                        disabled={q.options.length <= 2}
                                      >
                                        <TrashIcon />
                                      </IconBtn>
                                    </OptionActions>
                                  </OptionRow>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </OptionsList>
                        )}
                      </Droppable>
                      <AddTagButton
                        type="button"
                        disabled={q.options.length >= MAX_ANSWER_OPTIONS}
                        onClick={() => addOption(q.id)}
                      >
                        <PlusIconThin />
                        {t('modal.test_create.questions.add_option')}
                      </AddTagButton>
                    </FormBlock>
                  ) : null}

                  {showExplanation ? (
                    <FormBlock>
                      <LabelRow direction="row" align="center">
                        <InlineFieldLabel>
                          {t('modal.test_create.questions.explanation_label')}
                        </InlineFieldLabel>
                        <InfoIcon
                          stc
                          msg={t('modal.test_create.questions.explanation_hint')}
                        />
                      </LabelRow>
                      <ExplanationWrap>
                        <ExplanationTextarea
                          value={q.explanation}
                          maxLength={EXPLANATION_MAX}
                          onChange={(e) =>
                            updateQuestion(q.id, {
                              explanation: e.target.value.slice(
                                0,
                                EXPLANATION_MAX
                              )
                            })
                          }
                          placeholder={t(
                            'modal.test_create.questions.explanation_placeholder'
                          )}
                        />
                        <CharCounter>
                          {q.explanation.length}/{EXPLANATION_MAX}
                        </CharCounter>
                      </ExplanationWrap>
                    </FormBlock>
                  ) : null}
                </>
              )
            })()}

            <FormActions direction="row">
              <BrgButton
                variant={ButtonVariant.Hollow}
                width={132}
                onClick={() => removeQuestion(q.id)}
              >
                {t('modal.test_create.questions.delete_question')}
              </BrgButton>
              {isQuestionValidForSave(q) ? (
                <BrgButton
                  variant={ButtonVariant.Filled}
                  width={180}
                  disabled={savingQuestionId === q.id}
                  onClick={() => void saveQuestion(q.id)}
                >
                  {t('modal.test_create.questions.save_question')}
                </BrgButton>
              ) : null}
            </FormActions>
                        </>
                      )}
                    </QuestionFormCard>
                  )}
                </Draggable>
              ))}
              {listProvided.placeholder}
            </QuestionsDroppableList>
          )}
        </Droppable>
      </DragDropContext>

      <AddTagButton type="button" onClick={addQuestion}>
        <PlusIconThin />
        {t('modal.test_create.questions.add_question')}
      </AddTagButton>
    </>
  )
}

export default TestCreateQuestionsTab

